import {
  SkillBuilder_TriggerFragment,
  SkillBuilder_VerificationStepFragment,
} from "../SkillDetailPage.generated";
import { TriggerInput, VerificationStepType } from "@src/types.generated";
import { NumberTriggerCondition } from "@src/types.generated";

export const triggerIsInvalid = (
  trigger: SkillBuilder_TriggerFragment,
  parentStep: SkillBuilder_VerificationStepFragment,
): boolean => {
  const hasNoCondition =
    !trigger.numberCondition &&
    !trigger.yesNoCondition &&
    !trigger.checkedCondition &&
    !trigger.verificationStepMultipleChoiceCondition &&
    !trigger.verificationStepMultipleChoiceAnyCondition;
  if (hasNoCondition) return true;
  const optionIds = new Set(parentStep.responseOptions.map((o) => o.id));
  if (
    trigger.verificationStepMultipleChoiceCondition &&
    !optionIds.has(
      trigger.verificationStepMultipleChoiceCondition.stepResponseOption.id,
    )
  ) {
    return true;
  }
  const anyOptions =
    trigger.verificationStepMultipleChoiceAnyCondition?.options;
  if (anyOptions) {
    // Covers both shapes (no options + all options deleted) —
    // `[].every(...)` is `true`, so an empty array also flags as invalid.
    const everyMissing = anyOptions.every(
      (o) => !optionIds.has(o.stepResponseOption.id),
    );
    if (everyMissing) return true;
  }
  return false;
};

export const triggerToTriggerInput = (
  trigger: SkillBuilder_TriggerFragment,
): TriggerInput => {
  return {
    actions: trigger.actions.map((action) => ({
      flagAction: action.flagAction
        ? { value: action.flagAction.flag }
        : undefined,
      correctiveAction: action.correctiveAction
        ? { value: { en: action.correctiveAction.action.en } }
        : undefined,
      messageAction: action.messageAction
        ? {
            userSelection: {
              ...action.messageAction.userSelection,
              roleIds: action.messageAction.userSelection.roleMemberships.map(
                (roleMembership) => roleMembership.role.id,
              ),
              locationIds:
                action.messageAction.userSelection.locationMemberships.map(
                  (locationMembership) => locationMembership.location.id,
                ),
              locationGroupIds:
                action.messageAction.userSelection.locationGroupMemberships.map(
                  (locationGroupMembership) =>
                    locationGroupMembership.locationGroup.id,
                ),
              roleGroupIds:
                action.messageAction.userSelection.roleGroupMemberships.map(
                  (roleGroupMembership) => roleGroupMembership.roleGroup.id,
                ),
              userIds: action.messageAction.userSelection.userMemberships.map(
                (userMembership) => userMembership.user.id,
              ),
            },
            note: action.messageAction.note
              ? { value: { en: action.messageAction.note.en } }
              : undefined,
          }
        : undefined,
      followUpTaskAction: action.remediateAction
        ? {
            dueDateOffsetDays: action.remediateAction.dueDateOffsetDays,
            proofOfCompletionRequirement:
              action.remediateAction.proofOfCompletionRequirement,
            approvalRequired: action.remediateAction.approvalRequired,
          }
        : undefined,
    })),
    checkedCondition: trigger.checkedCondition
      ? { value: trigger.checkedCondition.checked }
      : undefined,
    numberCondition: trigger.numberCondition
      ? {
          condition: trigger.numberCondition.condition,
          number: { value: trigger.numberCondition.number },
          numberRangeTop: trigger.numberCondition.numberRangeTop
            ? { value: trigger.numberCondition.numberRangeTop }
            : undefined,
        }
      : undefined,
    verificationStepMultipleChoiceCondition:
      trigger.verificationStepMultipleChoiceCondition
        ? {
            stepResponseOptionId:
              trigger.verificationStepMultipleChoiceCondition.stepResponseOption
                .id,
            condition:
              trigger.verificationStepMultipleChoiceCondition.condition,
          }
        : undefined,
    verificationStepMultipleChoiceAnyCondition:
      trigger.verificationStepMultipleChoiceAnyCondition
        ? {
            options:
              trigger.verificationStepMultipleChoiceAnyCondition.options.map(
                (opt) => ({
                  stepResponseOptionId: opt.stepResponseOption.id,
                }),
              ),
          }
        : undefined,
    yesNoCondition: trigger.yesNoCondition
      ? { value: trigger.yesNoCondition.yesNo }
      : undefined,
  };
};

export const defaultTriggerInput = (
  verificationStepType?: VerificationStepType,
  stepResponseOptionIds?: string[],
  isRemediateAction?: boolean,
): TriggerInput => {
  return {
    actions: [
      isRemediateAction
        ? { followUpTaskAction: {} }
        : { flagAction: { value: true } },
    ],
    checkedCondition:
      verificationStepType === VerificationStepType.Checkbox
        ? { value: true }
        : undefined,
    numberCondition:
      verificationStepType === VerificationStepType.Number ||
      verificationStepType === VerificationStepType.Temperature
        ? {
            condition: NumberTriggerCondition.GreaterThan,
            number: { value: 0 },
          }
        : undefined,
    verificationStepMultipleChoiceCondition:
      verificationStepType === VerificationStepType.MultipleChoice
        ? {
            stepResponseOptionId: stepResponseOptionIds?.[0] || "",
            condition: true,
          }
        : undefined,
    yesNoCondition:
      verificationStepType === VerificationStepType.YesNo
        ? { value: true }
        : undefined,
  };
};
